
import { useCallback, useEffect, useReducer } from "react";
import { IMatchComments, MatchComments } from "@sky-it-livedata/livedata-sports-design-system";
import { useSubscription } from "../../hooks/useSubscription";
import { graphql } from "appsync";
import { WidgetContext } from "../../types/WidgetContext";
import { log } from "../../utils/log";
import { LmpMatchCommentsQuery, LmpMatchCommentsSubscription, IMatchCommentsSubscription } from "./types";

import transformQuery from "./transformers/query";
import transformSubscription from "./transformers/subscription";


import {
  ID_WRAPPER_COMMENTS,
  defaultMatchComments,
  getNewPostsIndex,
  getPageNumber,
  getUrlWithoutSearchParams,
  optionsScrollTo,
} from '../../utils/match-comments-helpers';

export const setCustomAttributes = ({
  variables,
}: WidgetContext<LmpMatchCommentsQuery>) => {
  return {
    variables: JSON.stringify(variables),
  };
};

const subscriptionDocument = graphql('subscription OnUpdateMatchCommentsByMatchIdAndPage($matchId: ID!, $page: Int!) {\n  onUpdateMatchCommentsByMatchIdAndPage(matchId: $matchId, page: $page) {\n    matchId\n    matchStatus\n    page\n    firstCommentDate\n    lastCommentDate\n    comments {\n      commentId\n      type\n      time\n      message\n      createdAt\n      updatedAt\n    }\n    pagination {\n      nextLink\n      prevLink\n      items\n      itemsPerPage\n    }\n  }\n}');

export const getServerProps = ({
  props
}: WidgetContext<LmpMatchCommentsQuery>): IMatchComments => {
  return transformQuery(props);
}

const LmpMatchCommentsWidget = ({
  props,
   variables,
  ...rest
}: WidgetContext<IMatchComments>) => {
  log({ props, variables, ...rest });

  const fallbackPage = Number(variables?.page ?? 1);
  const normalizedVariables = {
    ...variables,
    page: typeof window === 'undefined' ? fallbackPage : getPageNumber(fallbackPage),
  };

  const componentModel = useSubscription<
    IMatchCommentsSubscription,
    LmpMatchCommentsSubscription,
    typeof subscriptionDocument
  >({
    initialData: props,
    graphQLDocument: subscriptionDocument,
    variables: normalizedVariables,
    transform: transformSubscription,
    mergeData: true,
  }) as IMatchComments;

    const reducerMatchComments = (prev: IMatchComments, next: IMatchComments): IMatchComments => {
    const latestPostIndex: number = getNewPostsIndex(prev.comments ?? [], next.comments ?? []);
    const isBadgeReset: boolean =
      prev.updatesBadge && next.updatesBadge ? prev.updatesBadge.isVisible! && !next.updatesBadge.isVisible! : false;

    return {
      ...next,
      newPostsCount: (prev.newPostsCount ?? 0) + latestPostIndex,
      updatesBadge: {
        isVisible: isBadgeReset ? false : latestPostIndex > 0,
        count: isBadgeReset ? 0 : (prev.updatesBadge?.count ?? 0) + latestPostIndex,
        onClick: () => onClickBadge(next),
      },
    };
  };

  const [updatedPayload, updatePayload] = useReducer(reducerMatchComments, componentModel);

  const onClickBadge = useCallback((payload: IMatchComments) => {
    const { updatesBadge } = defaultMatchComments;
    updatePayload({
      ...payload,
      updatesBadge,
    });
    
    if (getPageNumber(1) === 1) {
      const elementComments: HTMLElement | null = document.getElementById(ID_WRAPPER_COMMENTS);
      elementComments && elementComments.scrollIntoView(optionsScrollTo);
    } else {
      location.replace(getUrlWithoutSearchParams());
    }
  }, []); 

  useEffect(() => {
    updatePayload(componentModel);
  }, [componentModel]);

  return (
      <MatchComments {...updatedPayload} />
  );
}

export default LmpMatchCommentsWidget;